import React, { useState, useEffect } from 'react';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, 
  isSameDay, isWithinInterval, parseISO, isValid, isBefore, addDays } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronLeft, FaChevronRight, FaCalendarAlt, FaCheck } from 'react-icons/fa';

export default function MiniCalendar({ 
  onSelectRange, 
  onClose, 
  initialStartDate = new Date(),
  initialEndDate = null,
  minSelectableDays = 1,
  maxSelectableDays = 4,
  preventPastDates = false,
  allowLongRanges = false,
  maxRangeDays = null
}) {
  const [currentMonth, setCurrentMonth] = useState(startOfMonth(new Date()));
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate || initialStartDate);
  const [selectionMode, setSelectionMode] = useState('start'); // 'start' or 'end'
  const [selectionError, setSelectionError] = useState(''); // To display error messages
  
  // Get today's date for comparison
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to start of day

  // Effect to validate and fix date range
  useEffect(() => {
    // Ensure end date is not before start date
    if (isBefore(endDate, startDate)) {
      setEndDate(startDate);
    }
    
    // Skip max day validation if allowLongRanges is true
    if (!allowLongRanges) {
      // Use maxRangeDays if provided, otherwise use maxSelectableDays
      const effectiveMaxDays = maxRangeDays || maxSelectableDays;
      
      // Ensure date range doesn't exceed max days
      const dayDiff = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24));
      if (dayDiff > effectiveMaxDays - 1) {
        setEndDate(addDays(startDate, effectiveMaxDays - 1));
        setSelectionError(`Date range cannot exceed ${effectiveMaxDays} days`);
      } else {
        setSelectionError('');
      }
    } else {
      // Clear any selection errors if long ranges are allowed
      setSelectionError('');
    }
    
    // If we need to prevent past dates, check if start date is in the past
    if (preventPastDates && isBefore(startDate, today)) {
      setSelectionError('Cannot select dates in the past');
    }
  }, [startDate, endDate, maxSelectableDays, preventPastDates, allowLongRanges, maxRangeDays]);

  const handleNextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const handlePrevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const handleDateClick = (day) => {
    // Check if selecting past date when restricted
    if (preventPastDates && isBefore(day, today)) {
      setSelectionError('Cannot select dates in the past');
      return;
    }
    
    if (selectionMode === 'start') {
      setStartDate(day);
      setEndDate(day);
      setSelectionMode('end');
      setSelectionError(''); // Clear any errors when starting a new selection
    } else {
      // End date selection
      if (isBefore(day, startDate)) {
        // If user clicks before start date, swap the selection
        // But first check if the new start date would be in the past
        if (preventPastDates && isBefore(day, today)) {
          setSelectionError('Cannot select dates in the past');
          return;
        }
        setEndDate(startDate);
        setStartDate(day);
      } else {
        // Only check max days if allowLongRanges is false
        if (!allowLongRanges) {
          // Use maxRangeDays if provided, otherwise use maxSelectableDays
          const effectiveMaxDays = maxRangeDays || maxSelectableDays;
          
          // Calculate days difference to check against max
          const daysDiff = Math.round((day - startDate) / (1000 * 60 * 60 * 24));
          
          if (daysDiff > effectiveMaxDays - 1) {
            // Don't update the end date, just show error
            setSelectionError(`Maximum ${effectiveMaxDays} days allowed. Try a shorter range.`);
            // Add animation pulse to the message
            const errorElement = document.querySelector('.error-message');
            if (errorElement) {
              errorElement.classList.add('animate-pulse');
              setTimeout(() => {
                errorElement.classList.remove('animate-pulse');
              }, 1000);
            }
            return;
          }
        }
        
        setEndDate(day);
        setSelectionError('');
      }
      setSelectionMode('start'); // Reset for next selection
    }
  };

  const handleConfirm = (e) => {
    // Stop event from bubbling up to parent elements
    if (e) e.stopPropagation();
    
    // Pass both start and end dates to the parent component
    console.log("Confirming selection:", { start: startDate, end: endDate });
    
    // Call parent callbacks
    if (onSelectRange) {
      onSelectRange(startDate, endDate);
    }
    if (onClose) {
      onClose();
    }
  };

  // Generate calendar days
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const monthDays = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Add empty slots for days of the week before the first day
  const firstDay = monthStart.getDay();
  const emptyDays = Array.from({ length: firstDay }, (_, i) => i);

  // Check if a day is in the selected range
  const isInRange = (day) => {
    return isWithinInterval(day, { start: startDate, end: endDate });
  };

  // Check if a day is the start or end date
  const isStartDate = (day) => isSameDay(day, startDate);
  const isEndDate = (day) => isSameDay(day, endDate);

  return (
    <motion.div 
      className="bg-[#1d2a46] rounded-lg shadow-[0_8px_30px_rgb(0,0,0,0.4)] border border-gray-700/50 px-4 py-3 w-[270px] relative"
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.9, opacity: 0 }}
      style={{
        boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.4), 0 0 10px 2px rgba(216, 108, 229, 0.15)"
      }}
    >
      {/* Calendar Header */}
      <div className="flex justify-between items-center mb-3">
        <button 
          onClick={handlePrevMonth}
          className="text-white/70 hover:text-white p-1.5 rounded-full hover:bg-gray-700/30 transition-all duration-200"
        >
          <FaChevronLeft />
        </button>
        <h3 className="text-white/90 font-medium">
          {format(currentMonth, 'MMMM yyyy')}
        </h3>
        <button 
          onClick={handleNextMonth}
          className="text-white/70 hover:text-white p-1.5 rounded-full hover:bg-gray-700/30 transition-all duration-200"
        >
          <FaChevronRight />
        </button>
      </div>
      
      {/* Day headers */}
      <div className="grid grid-cols-7 mb-1">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
          <div key={`header-${i}`} className="text-center text-xs text-gray-400 font-medium">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar body */}
      <div className="grid grid-cols-7 gap-1 mb-3">
        {/* Empty slots */}
        {emptyDays.map((_, index) => (
          <div 
            key={`empty-${index}`} 
            className="h-8 w-8"
          />
        ))}

        {/* Actual days */}
        {monthDays.map((day) => {
          const isStart = isStartDate(day);
          const isEnd = isEndDate(day);
          const inRange = isInRange(day);
          const isPast = preventPastDates && isBefore(day, today);
          const isToday = isSameDay(day, today);
          
          return (
            <motion.button
              key={format(day, 'yyyy-MM-dd')}
              whileHover={isPast ? {} : { scale: 1.05 }}
              whileTap={isPast ? {} : { scale: 0.95 }}
              onClick={isPast ? null : () => handleDateClick(day)}
              className={`
                h-8 w-8 rounded-full flex items-center justify-center text-sm relative
                transition-all duration-200
                ${isPast 
                  ? 'text-gray-600 cursor-not-allowed' 
                  : isStart || isEnd 
                    ? 'bg-gradient-to-r from-soft-pink to-mint-green text-pri-dark font-medium shadow-md' 
                    : inRange 
                      ? 'bg-gradient-to-r from-soft-pink/30 to-mint-green/30 text-white border border-gray-600/30'
                      : isToday
                        ? 'text-white font-medium ring-2 ring-mint-green/40'
                        : 'text-white hover:bg-gray-700/50 hover:border hover:border-gray-600/50'}
              `}
            >
              {format(day, 'd')}
            </motion.button>
          );
        })}
      </div>

      {/* Day count and confirmation */}
      <div className="mt-2">
        {/* Error message */}
        {selectionError && (
          <div className="mb-2 bg-red-900/30 text-red-300 text-xs p-2 rounded-lg text-center error-message border border-red-900/50 shadow-sm">
            {selectionError}
          </div>
        )}
        
        {/* Current day count */}
        <div className="flex justify-between items-center mb-2.5">
          <div className="text-white text-sm font-medium flex items-center">
            <span className="bg-gray-800/80 px-2.5 py-1 rounded-full border border-gray-700/50 shadow-sm">
              {Math.round((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1} day{Math.round((endDate - startDate) / (1000 * 60 * 60 * 24)) !== 0 ? 's' : ''} selected
            </span>
          </div>
          
          {!allowLongRanges && (
            <div className="flex items-center">
              <div className="w-20 h-2 bg-[#2a3b52] rounded-full overflow-hidden shadow-inner border border-gray-700/30">
                <div 
                  className={`h-full rounded-full ${
                    selectionError ? 'bg-red-400' : 'bg-gradient-to-r from-soft-pink to-mint-green'
                  }`}
                  style={{ 
                    width: `${Math.min(100, (Math.round((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1) / (maxRangeDays || maxSelectableDays) * 100)}%` 
                  }}
                ></div>
              </div>
              
              <span className="ml-2 text-xs text-gray-400">
                / {maxRangeDays || maxSelectableDays}
              </span>
            </div>
          )}
        </div>
        
        {/* Buttons */}
        <div className="flex gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="py-1.5 px-4 rounded-full text-gray-300 text-xs hover:bg-[#2a3b52] transition-all duration-200 border border-[#2a3b52] hover:border-gray-700"
          >
            Cancel
          </button>
          
          <button
            onClick={(e) => handleConfirm(e)}
            disabled={!!selectionError}
            className={`flex-1 py-1.5 rounded-full flex items-center justify-center font-medium text-xs
              ${selectionError 
                ? 'bg-[#2a3b52] text-gray-500 cursor-not-allowed' 
                : 'bg-gradient-to-r from-soft-pink to-mint-green text-pri-dark hover:opacity-90 transition-all duration-200 shadow-sm'
              }`}
          >
            <FaCheck className="mr-1" />
            Confirm
          </button>
        </div>
      </div>
    </motion.div>
  );
} 